import React, { useState, useEffect, useCallback } from 'react';
import FilterAutocomplete from '../../components/Common/FilterAutocomplete';
import { getOriginInfo } from '../../utils/originLabel';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Payment,
  TrendingUp,
  AccessTime,
  CheckCircle,
  Cancel,
  Refresh,
  FileDownload,
  Search,
  Clear,
  ContentCopy,
  Close,
  ReceiptLong,
  Email,
  SendOutlined,
  Undo,
  EditOutlined,
} from '@mui/icons-material';
import DataTable, { Column, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import { paymentsService, settingsService } from '../../services';
import { sispService } from '../../services/sisp.service';

interface TransactionRow {
  id: string;
  reference: string;
  merchantRef?: string;
  amount: number;
  status: string;
  paymentMethod: string;
  createdAt: string;
  customerName?: string;
  customerEmail?: string;
  entityName?: string;
  eventName?: string;
  invoiceReference?: string;
  source?: string;
  origin?: string;
  platform?: string;
}

interface TxStats {
  total: number;
  pagos: number;
  pendentes: number;
  valorTotal: number;
}

const TAB_STATUS_MAP = ['PAID', 'PENDING', 'FAILED', 'CANCELLED', 'REFUNDED', 'all'];

const Transacoes: React.FC = () => {
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('PAID');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [entidadeFilter, setEntidadeFilter] = useState('all');
  const [servicoFilter, setServicoFilter] = useState('all');
  const [eventoFilter, setEventoFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState<{ entidades: string[]; servicos: { codigo: string; label: string }[]; eventos: { id: string; label: string }[] }>({ entidades: [], servicos: [], eventos: [] });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  const [activeTab, setActiveTab] = useState(0);
  const [viewDialog, setViewDialog] = useState(false);
  const [selected, setSelected] = useState<TransactionRow | null>(null);

  const [stats, setStats] = useState<TxStats>({ total: 0, pagos: 0, pendentes: 0, valorTotal: 0 });

  const [emailDialog, setEmailDialog] = useState(false);
  const [emailRow, setEmailRow] = useState<TransactionRow | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const [resendingTicket, setResendingTicket] = useState<string | null>(null);
  const [resendFeedback, setResendFeedback] = useState<{ id: string; type: 'success' | 'error'; msg: string } | null>(null);
  const [resendConfirmRow, setResendConfirmRow] = useState<TransactionRow | null>(null);

  const [refundDialog, setRefundDialog] = useState<{ open: boolean; row: TransactionRow | null }>({ open: false, row: null });
  const [refundMotivo, setRefundMotivo] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const [editingEntityName, setEditingEntityName] = useState(false);
  const [entityNameInput, setEntityNameInput] = useState('');
  const [entityNameSaving, setEntityNameSaving] = useState(false);

  const handleTabChange = (_: any, newTab: number) => {
    setActiveTab(newTab);
    setStatusFilter(TAB_STATUS_MAP[newTab]);
    setPage(0);
  };

  const fetchStats = useCallback(async () => {
    try {
      const data = await paymentsService.getStats(30);
      if (data?.stats) {
        setStats({
          total: data.stats.total || 0,
          pagos: data.stats.paid || 0,
          pendentes: data.stats.pending || 0,
          valorTotal: data.stats.totalPaid || 0,
        });
      }
    } catch { /* silent */ }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // A aba Estornos lista OPERAÇÕES DE DEVOLUÇÃO, não pagamentos com estado
      // estornado. Filtrar pagamentos por status='REFUNDED' deixava-a sempre
      // vazia nas devoluções PARCIAIS — o pagamento continua pago, e bem.
      if (statusFilter === 'REFUNDED') {
        const r = await sispService.getRefunds({ limit: rowsPerPage, offset: page * rowsPerPage });
        setRows((r.data || []).map((d: any) => ({
          id: d.pagamento?.id || d.id,
          reference: d.merchantRef,
          merchantRef: d.merchantRef,
          amount: d.valor,
          // O estado da DEVOLUÇÃO, não o do pagamento.
          status: d.concluida ? 'REFUNDED' : d.estado === 'F' ? 'FAILED' : 'PENDING',
          paymentMethod: d.tipo === 'PARCIAL' ? 'Parcial' : 'Total',
          createdAt: d.criadaEm,
          entityName: d.motivo || '—',
          origin: d.pagamento?.origem,
          // Mostra de que pagamento saiu e quanto resta — a leitura que faltava.
          invoiceReference:
            `de ${Math.round(d.pagamento?.valor || 0).toLocaleString('pt-PT')}$ · ` +
            `devolvido ${Math.round(d.pagamento?.devolvido || 0).toLocaleString('pt-PT')}$ · ` +
            `resta ${Math.round(d.pagamento?.porDevolver || 0).toLocaleString('pt-PT')}$`,
        })));
        setTotalCount(r.pagination?.total || 0);
        return;
      }

      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) filters.search = search;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (sourceFilter !== 'all') filters.source = sourceFilter;
      if (methodFilter !== 'all') filters.paymentMethod = methodFilter;
      if (entidadeFilter !== 'all') filters.entidade = entidadeFilter;
      if (servicoFilter !== 'all') filters.servico = servicoFilter;
      if (eventoFilter !== 'all') filters.evento = eventoFilter;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (minAmount) filters.minAmount = Number(minAmount);
      if (maxAmount) filters.maxAmount = Number(maxAmount);

      const response = await paymentsService.getPayments(filters);

      const mapped: TransactionRow[] = (response.data || []).map((p: any) => ({
        id: p.id || '',
        reference: p.reference || '',
        merchantRef: p.merchantRef || p.reference || '',
        amount: p.amount || 0,
        status: p.status || '',
        paymentMethod: p.paymentMethod || '',
        createdAt: p.createdAt || '',
        customerName: p.customerName,
        customerEmail: p.customerEmail,
        entityName: p.entityName,
        eventName: p.eventName,
        invoiceReference: p.invoiceReference,
        source: p.source,
        origin: p.origin,
        platform: p.platform,
      }));

      setRows(mapped);
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.message || 'Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, sourceFilter, methodFilter, entidadeFilter, servicoFilter, eventoFilter, startDate, endDate, minAmount, maxAmount]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Opções dos filtros de entidade e serviço (carregadas uma vez)
  useEffect(() => {
    paymentsService.getFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const servicoOptions = React.useMemo(
    () => filterOptions.servicos.map((s) => ({ value: s.codigo, label: s.label })),
    [filterOptions.servicos],
  );

  const eventoOptions = React.useMemo(
    () => (filterOptions.eventos || []).map((e) => ({ value: e.id, label: e.label })),
    [filterOptions.eventos],
  );

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSourceFilter('all');
    setMethodFilter('all');
    setEntidadeFilter('all');
    setServicoFilter('all');
    setEventoFilter('all');
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setActiveTab(0);
    setPage(0);
  };

  const renderStatus = (value: string) => {
    const isPaid = ['N', 'PAID', 'PG', 'PAGO'].includes(value);
    const isFailed = ['F', 'FAILED', 'FL'].includes(value);
    const isCancelled = ['CANCELLED', 'CN', 'CANCELADO', 'EXPIRADO'].includes(value);
    const isRefunded = ['REFUNDED', 'E'].includes(value);
    const label = isPaid ? 'Pago' : isFailed ? 'Falhou' : isCancelled ? 'Cancelado' : isRefunded ? 'Estornado' : 'Pendente';
    const color = isPaid ? 'success' : isFailed ? 'error' : isCancelled ? 'default' : isRefunded ? 'secondary' : 'warning';
    return <Chip size="small" label={label} color={color as any} />;
  };

  const columns: Column<TransactionRow>[] = [
    { id: 'reference', label: 'Referência', minWidth: 120 },
    { id: 'customerName', label: 'Cliente', minWidth: 160, format: (v) => v || <span style={{ color: '#999' }}>-</span> },
    { id: 'entityName', label: 'Entidade', minWidth: 160, format: (v) => v || <span style={{ color: '#999' }}>-</span> },
    { id: 'eventName', label: 'Evento', minWidth: 140, format: (v) => v || <span style={{ color: '#999' }}>-</span> },
    { id: 'amount', label: 'Valor', minWidth: 110, align: 'right', format: (v) => <CurrencyCell value={v} /> },
    { id: 'paymentMethod', label: 'Método', minWidth: 100 },
    { id: 'origin' as any, label: 'Origem', minWidth: 130, format: (_v: any, row?: any) => {
      const { label, color } = getOriginInfo(row?.origin, row?.source, row?.referer, row?.platform);
      return <Chip size="small" variant="outlined" color={color as any} label={label} />;
    } },
    { id: 'createdAt', label: 'Data', minWidth: 140, format: (v) => <DateCell value={v} includeTime /> },
    { id: 'status', label: 'Estado', minWidth: 110, align: 'center', format: renderStatus },
  ];

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  const handleView = (row: TransactionRow) => { setSelected(row); setViewDialog(true); setEditingEntityName(false); };

  const isActionable = (status: string) => {
    const paid = ['N', 'PAID', 'PG', 'PAGO'].includes(status);
    const cancelled = ['CANCELLED', 'CN', 'CANCELADO', 'EXPIRADO'].includes(status);
    return !paid && !cancelled;
  };

  const isEventTicket = (row: TransactionRow) => {
    const origin = (row.origin || '').toLowerCase();
    // SISP event ticket transactions have origin = 'ticket_purchase', 'evt', etc.
    return (
      ['ticket_purchase', 'evt'].includes(origin) ||
      origin.includes('ticket')
    );
  };

  const isPaid = (status: string) => ['N', 'PAID', 'PG', 'PAGO'].includes(status);

  const isSisp = (row: TransactionRow) => (row.source || '').toLowerCase() === 'sisp';

  const handleSaveEntityName = async () => {
    if (!selected || !entityNameInput.trim()) return;
    setEntityNameSaving(true);
    try {
      const txKey = selected.invoiceReference || selected.id;
      await sispService.updateTransactionEntityName(txKey, entityNameInput.trim());
      setSelected({ ...selected, entityName: entityNameInput.trim() });
      setRows(prev => prev.map(r => r.id === selected.id ? { ...r, entityName: entityNameInput.trim() } : r));
      setEditingEntityName(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || 'Erro ao actualizar entidade');
    } finally {
      setEntityNameSaving(false);
    }
  };

  const handleRefundConfirm = async () => {
    if (!refundDialog.row) return;
    setRefundLoading(true);
    setRefundError(null);
    try {
      const response = await sispService.requestRefund(refundDialog.row.id, refundMotivo);
      if (response.success) {
        setRefundDialog({ open: false, row: null });
        setRefundMotivo('');
        fetchRows();
      } else {
        setRefundError(response.message || 'Erro ao processar estorno');
      }
    } catch (err: any) {
      setRefundError(err.message || 'Erro ao processar estorno');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleResendTicketEmail = async (row: TransactionRow) => {
    // Use row.id (SISP payment ID) — the backend looks up pg_evento_pedidos via pg_pagamentos_sisp JOIN
    const orderId = row.id;
    if (!orderId) return;
    setResendConfirmRow(null);
    setResendingTicket(orderId);
    setResendFeedback(null);
    try {
      await paymentsService.resendTicketEmail(orderId);
      setResendFeedback({ id: orderId, type: 'success', msg: `Email reenviado com sucesso` });
    } catch (err: any) {
      setResendFeedback({ id: orderId, type: 'error', msg: err.message || 'Erro ao reenviar' });
    } finally {
      setResendingTicket(null);
    }
  };

  const handleOpenEmailDialog = (row: TransactionRow) => {
    setEmailRow(row);
    setEmailInput(row.customerEmail || '');
    setEmailFeedback(null);
    setEmailDialog(true);
  };

  const handleSendSupportEmail = async () => {
    if (!emailInput.trim() || !emailRow) return;
    setEmailSending(true);
    setEmailFeedback(null);
    const isFailed = ['F', 'FAILED', 'FL'].includes(emailRow.status);
    const statusLabel = isFailed ? 'falhada' : 'pendente';
    const subject = `Pagali – Transação ${statusLabel} (${emailRow.reference})`;
    const contentHtml = `
      <p>Olá ${emailRow.customerName || 'Cliente'},</p>
      <p>Verificámos que a sua transação de <strong>${formatCurrency(Number(emailRow.amount) || 0)}</strong>
      (referência: <strong>${emailRow.reference}</strong>) ficou com o estado <strong>${statusLabel}</strong>.</p>
      <p>A nossa equipa está disponível para ajudar. Pode descrever a dificuldade que encontrou durante o pagamento?</p>
      <p>Pode responder diretamente a este email ou contactar o nosso suporte.</p>
      <p>Com os melhores cumprimentos,<br/>Equipa Pagali</p>
    `;
    try {
      await settingsService.sendEmail({ email: emailInput.trim(), subject, contentHtml });
      setEmailFeedback({ type: 'success', msg: `Email enviado para ${emailInput.trim()}` });
    } catch {
      setEmailFeedback({ type: 'error', msg: 'Erro ao enviar email. Tenta novamente.' });
    } finally {
      setEmailSending(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Transações</Typography>
          <Typography variant="body1" color="text.secondary">
            Registo completo de todas as transações processadas pelo Pagali
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>Exportar</Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchRows}>Atualizar</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card><CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                <ReceiptLong />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total (30 dias)</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card><CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                <CheckCircle />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats.pagos}</Typography>
                <Typography variant="body2" color="text.secondary">Pagas</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card><CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                <AccessTime />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats.pendentes}</Typography>
                <Typography variant="body2" color="text.secondary">Pendentes</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card><CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                <TrendingUp />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{formatCurrency(stats.valorTotal)}</Typography>
                <Typography variant="body2" color="text.secondary">Valor total pago</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth size="small"
                label="Pesquisar"
                placeholder="Referência, cliente, entidade..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField fullWidth size="small" select label="Estado" value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="PAID">Pago</MenuItem>
                <MenuItem value="PENDING">Pendente</MenuItem>
                <MenuItem value="FAILED">Falhado</MenuItem>
                <MenuItem value="CANCELLED">Cancelado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField fullWidth size="small" select label="Origem" value={sourceFilter}
                onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="sisp">SISP / Vinti4</MenuItem>
                <MenuItem value="legacy">Pagali</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField fullWidth size="small" select label="Método" value={methodFilter}
                onChange={(e) => { setMethodFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="VINTI4">Vinti4</MenuItem>
                <MenuItem value="MASTERCARD">Mastercard</MenuItem>
                <MenuItem value="VISA">Visa</MenuItem>
                <MenuItem value="MB">Multibanco</MenuItem>
                <MenuItem value="WALLET">Wallet Pagali</MenuItem>
                <MenuItem value="CASH">Numerário</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button fullWidth variant="outlined" startIcon={<Clear />} onClick={resetFilters} sx={{ height: 40 }}>
                Limpar
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FilterAutocomplete label="Entidade" value={entidadeFilter} options={filterOptions.entidades}
                onChange={(v) => { setEntidadeFilter(v); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FilterAutocomplete label="Serviço" value={servicoFilter} placeholderTodos="Todos"
                options={servicoOptions} onChange={(v) => { setServicoFilter(v); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FilterAutocomplete label="Evento" value={eventoFilter} placeholderTodos="Todos"
                options={eventoOptions} onChange={(v) => { setEventoFilter(v); setPage(0); }} />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Data início"
                value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Data fim"
                value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label="Valor mínimo (CVE)"
                value={minAmount} onChange={(e) => { setMinAmount(e.target.value); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label="Valor máximo (CVE)"
                value={maxAmount} onChange={(e) => { setMaxAmount(e.target.value); setPage(0); }} />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label={`Pagas (${stats.pagos})`} icon={<CheckCircle fontSize="small" color="success" />} iconPosition="start" />
          <Tab label="Pendentes" icon={<AccessTime fontSize="small" color="warning" />} iconPosition="start" />
          <Tab label="Falhadas" icon={<Cancel fontSize="small" color="error" />} iconPosition="start" />
          <Tab label="Canceladas" />
          <Tab label="Extornos" icon={<Undo fontSize="small" />} iconPosition="start" />
          <Tab label={`Todas (${stats.total})`} />
        </Tabs>
      </Paper>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
        onRefresh={fetchRows}
        onView={handleView}
        rowKey="id"
        emptyMessage="Nenhuma transação encontrada"
        extraActions={(row) => {
          const orderId = row.id;
          const feedback = resendFeedback?.id === orderId ? resendFeedback : null;
          return (
            <>
              {/* Reenviar bilhetes — apenas para transações pagas de eventos */}
              {isEventTicket(row) && isPaid(row.status) && (
                <Tooltip title={feedback?.type === 'success' ? feedback.msg : feedback?.type === 'error' ? feedback.msg : 'Reenviar email de bilhetes'}>
                  <span>
                    <IconButton
                      size="small"
                      color={feedback?.type === 'success' ? 'success' : feedback?.type === 'error' ? 'error' : 'primary'}
                      onClick={() => setResendConfirmRow(row)}
                      disabled={resendingTicket === orderId}
                    >
                      {resendingTicket === orderId
                        ? <CircularProgress size={16} color="inherit" />
                        : <SendOutlined fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {/* Estorno — apenas para transações SISP pagas */}
              {isSisp(row) && isPaid(row.status) && (
                <Tooltip title="Estornar pagamento">
                  <IconButton size="small" color="warning" onClick={() => { setRefundError(null); setRefundMotivo(''); setRefundDialog({ open: true, row }); }}>
                    <Undo fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Email de suporte — apenas para transações pendentes/falhadas */}
              {isActionable(row.status) && (
                <Tooltip title="Enviar email de suporte">
                  <IconButton size="small" color="warning" onClick={() => handleOpenEmailDialog(row)}>
                    <Email fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        }}
      />

      <Dialog open={emailDialog} onClose={() => !emailSending && setEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Enviar email de suporte
          <IconButton size="small" onClick={() => setEmailDialog(false)} disabled={emailSending}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {emailRow && (
            <Box>
              {emailFeedback && (
                <Alert severity={emailFeedback.type} sx={{ mb: 2 }}>{emailFeedback.msg}</Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vai ser enviado um email ao cliente a perguntar sobre a dificuldade no pagamento.
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: 600, width: 130 }}>Cliente</TableCell><TableCell>{emailRow.customerName || '-'}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Referência</TableCell><TableCell><code>{emailRow.reference}</code></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Valor</TableCell><TableCell>{formatCurrency(Number(emailRow.amount) || 0)}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Estado</TableCell><TableCell>{renderStatus(emailRow.status)}</TableCell></TableRow>
                </TableBody>
              </Table>
              <TextField
                fullWidth
                size="small"
                label="Email do cliente"
                placeholder="email@exemplo.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                disabled={emailSending || emailFeedback?.type === 'success'}
                helperText={!emailRow.customerEmail ? 'Email não disponível — podes inserir manualmente.' : undefined}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)} disabled={emailSending}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <Email />}
            onClick={handleSendSupportEmail}
            disabled={emailSending || emailFeedback?.type === 'success' || !emailInput.trim()}
          >
            {emailSending ? 'A enviar...' : 'Enviar email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalhes da Transação
          <IconButton size="small" onClick={() => setViewDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {formatCurrency(Number(selected.amount) || 0)}
                </Typography>
                <Box sx={{ mt: 1 }}>{renderStatus(selected.status)}</Box>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 160 }}>Referência</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code>{selected.reference}</code>
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(selected.reference)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {selected.invoiceReference && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Ref. Interna</TableCell>
                      <TableCell><code>{selected.invoiceReference}</code></TableCell>
                    </TableRow>
                  )}
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell><TableCell>{selected.customerName || '-'}</TableCell></TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Entidade</TableCell>
                    <TableCell>
                      {editingEntityName ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField size="small" value={entityNameInput} onChange={e => setEntityNameInput(e.target.value)} sx={{ flex: 1 }} autoFocus />
                          <Button size="small" variant="contained" onClick={handleSaveEntityName} disabled={entityNameSaving}>
                            {entityNameSaving ? '...' : 'Guardar'}
                          </Button>
                          <Button size="small" onClick={() => setEditingEntityName(false)}>Cancelar</Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{selected.entityName || '-'}</span>
                          <Tooltip title="Editar nome da entidade">
                            <IconButton size="small" onClick={() => { setEntityNameInput(selected.entityName || ''); setEditingEntityName(true); }}>
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Método</TableCell><TableCell>{selected.paymentMethod || '-'}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Origem</TableCell><TableCell>{selected.origin || '-'}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Plataforma</TableCell><TableCell>{selected.platform || '-'}</TableCell></TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                    <TableCell>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de estorno SISP */}
      <Dialog open={refundDialog.open} onClose={() => !refundLoading && setRefundDialog({ open: false, row: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Undo /> Solicitar Estorno
        </DialogTitle>
        <DialogContent dividers>
          {refundError && <Alert severity="error" sx={{ mb: 2 }}>{refundError}</Alert>}
          {refundDialog.row && (
            <Stack spacing={2}>
              <Alert severity="warning">Tem a certeza que deseja estornar este pagamento?</Alert>
              <Table size="small">
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: 600, width: 130 }}>Referência</TableCell><TableCell><code>{refundDialog.row.reference}</code></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell><TableCell>{refundDialog.row.customerName || '-'}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Valor</TableCell><TableCell><strong>{formatCurrency(Number(refundDialog.row.amount))}</strong></TableCell></TableRow>
                </TableBody>
              </Table>
              <TextField
                fullWidth multiline rows={3}
                label="Motivo do Estorno"
                placeholder="Descreva o motivo do estorno..."
                value={refundMotivo}
                onChange={(e) => setRefundMotivo(e.target.value)}
                disabled={refundLoading}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog({ open: false, row: null })} disabled={refundLoading}>Cancelar</Button>
          <Button
            variant="contained" color="error"
            startIcon={refundLoading ? <CircularProgress size={18} color="inherit" /> : <Undo />}
            onClick={handleRefundConfirm}
            disabled={refundLoading}
          >
            Confirmar Estorno
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de reenvio de bilhetes */}
      <Dialog open={!!resendConfirmRow} onClose={() => setResendConfirmRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Reenviar email de bilhetes
          <IconButton size="small" onClick={() => setResendConfirmRow(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {resendConfirmRow && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Vai ser reenviado o email de confirmação de bilhetes para:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {resendConfirmRow.customerName || '—'}
              </Typography>
              {resendConfirmRow.customerEmail && (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {resendConfirmRow.customerEmail}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                BCC: suporte@pagali.cv
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendConfirmRow(null)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SendOutlined />}
            onClick={() => resendConfirmRow && handleResendTicketEmail(resendConfirmRow)}
          >
            Confirmar envio
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transacoes;
